import { useEffect, useId, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import {
  useStrategyPricing,
  type StrategyPricingOverrideDto,
  type StrategyPricingVersionDto,
} from "@/hooks/useStrategyPricing";
import { useTenants, type Tenant } from "@/hooks/useTenants";
import type { SetPricingOverrideRequest } from "@/lib/api";
import { formatDate, formatMoneyFromString } from "@/utils/format";

const DEFAULT_CURRENCY = "USD";

// The server translates both the partial-unique-index race and a duplicate
// tenantId in the submitted override array to HTTP 409 with this message
// (see StrategyPricingAdminService.updatePricing) — handleResponse discards
// response.status (see useBlockAccounts), so this substring is the only
// client-side signal available without a broader api.ts refactor.
const CONCURRENT_EDIT_HINT = "updated concurrently";

interface OverrideFormRow {
  /** Stable React key only — the override's own `id` if editing an existing
   *  row, or a locally generated id for a newly added row. Never sent as-is;
   *  the PUT body only carries tenantId/price/promo. */
  key: string;
  tenantId: string;
  resellerRetailPriceMonthly: string;
  promoCode: string;
  promoDiscountPct: string;
  /** `<input type="datetime-local">` value, not ISO — converted at submit
   *  (unless untouched, see `promoExpiresAtOriginalIso` below). */
  promoExpiresAt: string;
  /** Full-precision ISO string as loaded from the server, or `null` for a
   *  new row / no promo. `datetime-local` only has minute precision, so
   *  round-tripping an untouched value through it would silently truncate
   *  seconds (e.g. `23:59:59` → `23:59:00`) on every Save — this is sent
   *  verbatim instead as long as `promoExpiresAtDirty` is false. */
  promoExpiresAtOriginalIso: string | null;
  /** True once the operator edits this row's promo-expiry input. */
  promoExpiresAtDirty: boolean;
}

function toFormRow(o: StrategyPricingOverrideDto): OverrideFormRow {
  return {
    key: o.id,
    tenantId: o.tenantId,
    resellerRetailPriceMonthly: o.resellerRetailPriceMonthly,
    promoCode: o.promo?.code ?? "",
    promoDiscountPct: o.promo?.discountPct ?? "",
    promoExpiresAt: o.promo?.expiresAt ? isoToDatetimeLocal(o.promo.expiresAt) : "",
    promoExpiresAtOriginalIso: o.promo?.expiresAt ?? null,
    promoExpiresAtDirty: false,
  };
}

function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(value: string): string {
  return new Date(value).toISOString();
}

let rowIdCounter = 0;
function newRowKey(): string {
  rowIdCounter += 1;
  return `new-${rowIdCounter}`;
}

// --- Client-side validation (mirrors the API's DTO validators) ----------
// Rejecting obviously-malformed input here saves an operator a round trip
// to the 400 response, and — for the promo expiry — prevents an unparseable
// `datetime-local` value from throwing a RangeError out of
// `new Date().toISOString()` before any try/catch is listening (see
// `buildValidatedOverrides` below, called from inside handleSave's `try`).

const PRICE_RE = /^\d{1,10}(\.\d{1,2})?$/;
const PERCENT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const CURRENCY_RE = /^[A-Z]{3}$/;

function isValidPrice(value: string): boolean {
  return PRICE_RE.test(value.trim());
}

// Discount % is a plain numeric percentage, not a currency amount — this
// codebase's parseFloat ban (see formatMoneyFromString) is specifically
// about avoiding float-coerced *money* strings, so a range check here is
// fine; nothing derived from it is displayed or sent as a money value.
function isValidPercent(value: string): boolean {
  const trimmed = value.trim();
  return PERCENT_RE.test(trimmed) && parseFloat(trimmed) <= 100;
}

function isValidExpiryInput(value: string): boolean {
  return !Number.isNaN(new Date(value).getTime());
}

class PricingValidationError extends Error {}

const priceFieldMessage = (field: string, example: string) =>
  `${field} must be a plain number with up to 2 decimal places (e.g. "${example}") — no currency symbols or commas.`;

/**
 * Validates the base-price/currency fields and every override row, then
 * returns the fully-built override list. Throws `PricingValidationError`
 * with a human message on the *first* violation found — callers should
 * catch it the same way as a save failure (see handleSave).
 */
function buildValidatedOverrides(
  rows: OverrideFormRow[],
  tenantName: (tenantId: string) => string
): SetPricingOverrideRequest[] {
  const seenTenants = new Set<string>();
  const overrides: SetPricingOverrideRequest[] = [];

  for (const row of rows) {
    if (!row.tenantId) {
      throw new PricingValidationError("Every override row needs a tenant selected.");
    }
    if (seenTenants.has(row.tenantId)) {
      throw new PricingValidationError(
        `Duplicate override for ${tenantName(row.tenantId)} — each tenant may appear once.`
      );
    }
    seenTenants.add(row.tenantId);

    if (!row.resellerRetailPriceMonthly.trim()) {
      throw new PricingValidationError(
        `Reseller retail price is required for ${tenantName(row.tenantId)}.`
      );
    }
    if (!isValidPrice(row.resellerRetailPriceMonthly)) {
      throw new PricingValidationError(
        priceFieldMessage(
          `Reseller retail price for ${tenantName(row.tenantId)}`,
          "179.99"
        )
      );
    }

    // Promo fields are all-or-none (DB CHECK mirrors this) — if any of the
    // three is filled, all three are required.
    const code = row.promoCode.trim();
    const discountPct = row.promoDiscountPct.trim();
    const expiresAtInput = row.promoExpiresAt.trim();
    const filledCount = [code, discountPct, expiresAtInput].filter(Boolean).length;
    if (filledCount > 0 && filledCount < 3) {
      const missing = [
        !code && "code",
        !discountPct && "discount %",
        !expiresAtInput && "expiry",
      ]
        .filter((x): x is string => !!x)
        .join(", ");
      throw new PricingValidationError(
        `Promo for ${tenantName(row.tenantId)} is missing: ${missing}. Promo code/discount %/expiry are all-or-none.`
      );
    }

    let promo: SetPricingOverrideRequest["promo"] = null;
    if (filledCount === 3) {
      if (!isValidPercent(discountPct)) {
        throw new PricingValidationError(
          `Discount % for ${tenantName(row.tenantId)} must be a number between 0 and 100 with up to 2 decimal places.`
        );
      }
      if (!isValidExpiryInput(expiresAtInput)) {
        throw new PricingValidationError(
          `Promo expiry for ${tenantName(row.tenantId)} is not a valid date.`
        );
      }
      // Untouched since load → send the original full-precision ISO
      // verbatim rather than round-tripping through minute-precision
      // datetime-local (see OverrideFormRow.promoExpiresAtOriginalIso).
      const expiresAt =
        !row.promoExpiresAtDirty && row.promoExpiresAtOriginalIso
          ? row.promoExpiresAtOriginalIso
          : datetimeLocalToIso(expiresAtInput);
      promo = { code, discountPct, expiresAt };
    }

    overrides.push({
      tenantId: row.tenantId,
      resellerRetailPriceMonthly: row.resellerRetailPriceMonthly.trim(),
      promo,
    });
  }

  return overrides;
}

/**
 * SFWL-014: super-admin pricing editor for a single strategy's retail/
 * wholesale base prices and per-tenant reseller overrides (with optional
 * promo). Not in nav — direct-link only
 * (`/admin/strategies/:strategyId/pricing`). The server's `SovrunAdminGuard`
 * is the real access-control gate; this page does no client-side role
 * checks and surfaces a 403 via toast + inline text.
 *
 * This outer component's only job is reading `:strategyId` and keying the
 * form by it. React Router keeps this route's component instance mounted
 * across SPA navigations between e.g. `/admin/strategies/A/pricing` and
 * `/B/pricing` — without the `key`, all of `AdminStrategyPricingForm`'s
 * local edit state (retailInput, overridesInput, etc.) would survive the
 * param change too, so a Save fired right after navigating could silently
 * apply strategy A's in-progress edits to strategy B. Keying by strategyId
 * forces React to unmount/remount the form, resetting that state for free.
 */
export default function AdminStrategyPricing() {
  const { strategyId } = useParams<{ strategyId: string }>();
  return <AdminStrategyPricingForm key={strategyId ?? "none"} strategyId={strategyId} />;
}

function AdminStrategyPricingForm({ strategyId }: { strategyId?: string }) {
  const idPrefix = useId();

  const { current, history, isLoading, error, refetch, setPricing, isSaving } =
    useStrategyPricing(strategyId);
  // This admin surface needs every tenant in the dropdown, not just the
  // default first page — useTenants (app/hooks/useTenants.ts) already
  // accepts a `limit` filter, so request a high cap here rather than
  // touching the hook itself.
  const { data: tenantsData } = useTenants({ limit: 200 });
  const tenants: Tenant[] = tenantsData?.tenants ?? [];

  // Derive-from-props pattern (`override ?? data`), not a mount-time
  // `useState(!data)` — a quick Save while the GET is still pending (or
  // failed) must not overwrite real pricing with empty defaults. Mirrors
  // AdminBlockAccounts's DailyExportSection.
  const [retailInput, setRetailInput] = useState<string | null>(null);
  const [wholesaleInput, setWholesaleInput] = useState<string | null>(null);
  const [currencyInput, setCurrencyInput] = useState<string | null>(null);
  const [overridesInput, setOverridesInput] = useState<OverrideFormRow[] | null>(null);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Query errors (incl. the server's 403 for non-super-admins) surface as a
  // toast in addition to the inline message below — a side effect, not
  // setState-in-effect, so it's safe to fire on every `error` change.
  const isPricingPending = !!strategyId && isLoading;
  const isPricingUnavailable = isPricingPending || (!!strategyId && error !== null);

  const retailValue = retailInput ?? current?.retailPriceMonthly ?? "";
  const wholesaleValue = wholesaleInput ?? current?.wholesalePriceMonthly ?? "";
  const currencyValue = currencyInput ?? current?.currency ?? DEFAULT_CURRENCY;
  const overrideRows: OverrideFormRow[] =
    overridesInput ?? (current?.overrides ?? []).map(toFormRow);

  const tenantName = (tenantId: string): string =>
    tenants.find((t) => t.id === tenantId)?.name ?? tenantId;

  const addOverrideRow = () => {
    setOverridesInput([
      ...overrideRows,
      {
        key: newRowKey(),
        tenantId: "",
        resellerRetailPriceMonthly: "",
        promoCode: "",
        promoDiscountPct: "",
        promoExpiresAt: "",
        promoExpiresAtOriginalIso: null,
        promoExpiresAtDirty: false,
      },
    ]);
  };

  const removeOverrideRow = (key: string) => {
    setOverridesInput(overrideRows.filter((r) => r.key !== key));
  };

  const updateOverrideRow = (key: string, patch: Partial<OverrideFormRow>) => {
    setOverridesInput(overrideRows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const resetEdits = () => {
    setRetailInput(null);
    setWholesaleInput(null);
    setCurrencyInput(null);
    setOverridesInput(null);
  };

  const handleSave = async () => {
    if (!strategyId) return;

    // Everything — validation, override building/ISO conversion, and the
    // PUT itself — lives inside this one try. Previously the override loop
    // (with its `datetimeLocalToIso` call) ran *before* the try started, so
    // an unparseable datetime-local value threw a RangeError straight out
    // of handleSave: unhandled rejection, no toast, Save silently did
    // nothing. Validating first means the conversion below is guaranteed
    // safe, but keeping it inside try is defense-in-depth against any other
    // unexpected throw in this block.
    try {
      if (!retailValue.trim()) {
        throw new PricingValidationError("Retail price is required.");
      }
      if (!isValidPrice(retailValue)) {
        throw new PricingValidationError(priceFieldMessage("Retail price", "199.99"));
      }
      if (!wholesaleValue.trim()) {
        throw new PricingValidationError("Wholesale price is required.");
      }
      if (!isValidPrice(wholesaleValue)) {
        throw new PricingValidationError(priceFieldMessage("Wholesale price", "149.99"));
      }
      const currency = (currencyValue.trim() || DEFAULT_CURRENCY).toUpperCase();
      if (!CURRENCY_RE.test(currency)) {
        throw new PricingValidationError(
          'Currency must be a 3-letter code (e.g. "USD").'
        );
      }

      const overrides = buildValidatedOverrides(overrideRows, tenantName);

      await setPricing({
        retailPriceMonthly: retailValue.trim(),
        wholesalePriceMonthly: wholesaleValue.trim(),
        currency,
        overrides,
      });
      toast.success("Pricing saved.");
      // setPricing's onSuccess already invalidated the query, so clearing
      // the overrides here re-derives from the fresh cache value.
      resetEdits();
    } catch (e) {
      const message = (e as Error).message;
      if (message.includes(CONCURRENT_EDIT_HINT)) {
        toast.error("Pricing was updated concurrently — reloading");
        resetEdits();
        await refetch();
      } else {
        // Preserve the operator's in-progress edits on any other error,
        // including a validation failure — the operator should be able to
        // fix the flagged field without losing everything else they typed.
        toast.error(message);
      }
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <title>Strategy Pricing | Sovrun</title>
      <h1 className="mb-2 text-2xl font-bold text-slate-900">Strategy Pricing</h1>
      <p className="mb-6 text-sm text-slate-500">
        Super-admin only. Retail / wholesale base prices and per-tenant reseller overrides
        for strategy {strategyId}.
      </p>

      {error && (
        <p className="mb-4 text-sm text-red-600">
          Couldn&apos;t load pricing: {error.message}
        </p>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Base Prices</h2>
        <div className="mb-4 flex flex-wrap gap-3">
          <div>
            <label
              htmlFor={`${idPrefix}-retail`}
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Retail $/mo
            </label>
            <input
              id={`${idPrefix}-retail`}
              value={retailValue}
              onChange={(e) => setRetailInput(e.target.value)}
              disabled={isPricingPending}
              placeholder="199.99"
              className="w-32 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            />
          </div>
          <div>
            <label
              htmlFor={`${idPrefix}-wholesale`}
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Wholesale $/mo
            </label>
            <input
              id={`${idPrefix}-wholesale`}
              value={wholesaleValue}
              onChange={(e) => setWholesaleInput(e.target.value)}
              disabled={isPricingPending}
              placeholder="149.99"
              className="w-32 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            />
          </div>
          <div>
            <label
              htmlFor={`${idPrefix}-currency`}
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Currency
            </label>
            <input
              id={`${idPrefix}-currency`}
              value={currencyValue}
              onChange={(e) => setCurrencyInput(e.target.value.toUpperCase())}
              disabled={isPricingPending}
              placeholder={DEFAULT_CURRENCY}
              className="w-20 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            />
          </div>
        </div>

        <h2 className="mb-3 text-lg font-semibold text-slate-900">Reseller Overrides</h2>
        <div className="mb-3 flex flex-col gap-3">
          {overrideRows.length === 0 && (
            <p className="text-sm text-slate-400 italic">No reseller overrides yet.</p>
          )}
          {overrideRows.map((row) => (
            <OverrideRow
              key={row.key}
              row={row}
              tenants={tenants}
              disabled={isPricingPending}
              onChange={(patch) => updateOverrideRow(row.key, patch)}
              onRemove={() => removeOverrideRow(row.key)}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={addOverrideRow}
          disabled={isPricingPending}
          className="mb-4 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
        >
          + Add override
        </button>

        <div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isPricingUnavailable}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? "Saving…" : isPricingPending ? "Loading…" : "Save"}
          </button>
        </div>
      </div>

      <HistorySection history={history} />
    </div>
  );
}

function OverrideRow({
  row,
  tenants,
  disabled,
  onChange,
  onRemove,
}: {
  row: OverrideFormRow;
  tenants: Tenant[];
  disabled: boolean;
  onChange: (patch: Partial<OverrideFormRow>) => void;
  onRemove: () => void;
}) {
  const idPrefix = useId();

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
      <div>
        <label
          htmlFor={`${idPrefix}-tenant`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Tenant
        </label>
        <select
          id={`${idPrefix}-tenant`}
          value={row.tenantId}
          onChange={(e) => onChange({ tenantId: e.target.value })}
          disabled={disabled}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
        >
          <option value="">Select tenant…</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-reseller-price`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Reseller retail $/mo
        </label>
        <input
          id={`${idPrefix}-reseller-price`}
          value={row.resellerRetailPriceMonthly}
          onChange={(e) => onChange({ resellerRetailPriceMonthly: e.target.value })}
          disabled={disabled}
          placeholder="179.99"
          className="w-32 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-promo-code`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Promo code
        </label>
        <input
          id={`${idPrefix}-promo-code`}
          value={row.promoCode}
          onChange={(e) => onChange({ promoCode: e.target.value })}
          disabled={disabled}
          placeholder="LAUNCH25"
          className="w-28 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-promo-discount`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Discount %
        </label>
        <input
          id={`${idPrefix}-promo-discount`}
          value={row.promoDiscountPct}
          onChange={(e) => onChange({ promoDiscountPct: e.target.value })}
          disabled={disabled}
          placeholder="25.00"
          className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-promo-expires`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Promo expires
        </label>
        <input
          id={`${idPrefix}-promo-expires`}
          type="datetime-local"
          value={row.promoExpiresAt}
          onChange={(e) =>
            onChange({ promoExpiresAt: e.target.value, promoExpiresAtDirty: true })
          }
          disabled={disabled}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
      >
        Remove
      </button>
    </div>
  );
}

function HistorySection({ history }: { history: StrategyPricingVersionDto[] }) {
  return (
    <div className="mt-10">
      <h2 className="mb-2 text-lg font-semibold text-slate-900">History</h2>
      <p className="mb-4 text-sm text-slate-500">
        Every prior version (AC3: closed, never mutated in place). The live version has no
        &quot;Effective To&quot; date.
      </p>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Effective From</th>
              <th className="px-4 py-2">Effective To</th>
              <th className="px-4 py-2">Retail</th>
              <th className="px-4 py-2">Wholesale</th>
              <th className="px-4 py-2">Currency</th>
              <th className="px-4 py-2">Overrides</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {history.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400 italic">
                  No pricing history yet.
                </td>
              </tr>
            )}
            {history.map((v) => (
              <tr key={v.id}>
                <td className="px-4 py-2">{formatDate(v.effectiveFrom)}</td>
                <td className="px-4 py-2">
                  {v.effectiveTo ? formatDate(v.effectiveTo) : "Live"}
                </td>
                <td className="px-4 py-2">
                  {formatMoneyFromString(v.retailPriceMonthly, v.currency)}
                </td>
                <td className="px-4 py-2">
                  {formatMoneyFromString(v.wholesalePriceMonthly, v.currency)}
                </td>
                <td className="px-4 py-2">{v.currency}</td>
                <td className="px-4 py-2">{v.overrides.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
